import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, AlertCircle, School, User, KeyRound } from "lucide-react";
import apiService from '@/services/api';
import axios from 'axios';

const API_URL = '';

export default function ImprovedLoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  // Warm up the database when login page loads (prevents cold start on actual login)
  useEffect(() => {
    fetch(`${API_URL}/api/warmup`).catch(() => {});
    fetch(`${API_URL}/api/public/schools-with-teachers`).catch(() => {});
  }, []);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(searchParams.get('expired') ? 'Your session has expired. Please sign in again.' : '');
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [password, setPassword] = useState('');
  const [teachers, setTeachers] = useState([]);
  const navigate = useNavigate();

  // Fetch schools and teachers on mount
  const fetchSchoolsWithTeachers = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API_URL}/api/public/schools-with-teachers`);
      const fetchedSchools = response.data.schools || [];
      setSchools(fetchedSchools);
      setLoadingSchools(false);

      // Cache school data for offline fallback
      localStorage.setItem('cachedSchoolData', JSON.stringify(fetchedSchools));

      // Restore last selected school from localStorage
      const savedSchool = localStorage.getItem('lastSelectedSchool');
      if (savedSchool && fetchedSchools.some(s => s.id === savedSchool)) {
        setSelectedSchool(savedSchool);
      }
    } catch (err) {
      console.error('Error fetching schools:', err);
      // Auto-retry up to 5 times with 2s delay
      if (retryCount < 5) {
        setTimeout(() => fetchSchoolsWithTeachers(retryCount + 1), 2000);
      } else {
        // Fall back to cached data
        const cached = localStorage.getItem('cachedSchoolData');
        if (cached) {
          try {
            const cachedSchools = JSON.parse(cached);
            setSchools(cachedSchools);
            const savedSchool = localStorage.getItem('lastSelectedSchool');
            if (savedSchool && cachedSchools.some(s => s.id === savedSchool)) {
              setSelectedSchool(savedSchool);
            }
          } catch (e) { /* ignore parse error */ }
        }
        setLoadingSchools(false);
      }
    }
  };

  // Load schools and teachers on mount - show cached data immediately, refresh in background
  useEffect(() => {
    // Show cached data instantly (no waiting for DB)
    const cached = localStorage.getItem('cachedSchoolData');
    if (cached) {
      try {
        const cachedSchools = JSON.parse(cached);
        if (cachedSchools.length > 0) {
          setSchools(cachedSchools);
          setLoadingSchools(false);
          const savedSchool = localStorage.getItem('lastSelectedSchool');
          if (savedSchool && cachedSchools.some(s => s.id === savedSchool)) {
            setSelectedSchool(savedSchool);
          }
        }
      } catch (e) { /* ignore parse error */ }
    }
    // Always refresh in background to pick up new teachers/schools
    fetchSchoolsWithTeachers();
  }, []);

  // Update teachers list when school changes
  const prevSchoolRef = useRef(selectedSchool);
  useEffect(() => {
    if (selectedSchool && schools.length > 0) {
      const school = schools.find(s => s.id === selectedSchool);
      if (school) {
        setTeachers(school.teachers || []);
        // Only reset teacher selection when school actually changes, not on schools data refresh
        if (prevSchoolRef.current !== selectedSchool) {
          setSelectedTeacher('');
          prevSchoolRef.current = selectedSchool;
        }
        localStorage.setItem('lastSelectedSchool', selectedSchool);
      } else {
        setTeachers([]);
        setSelectedTeacher('');
      }
    } else if (!selectedSchool) {
      setTeachers([]);
      setSelectedTeacher('');
    }
  }, [selectedSchool, schools]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedSchool) {
      setError('Please select a school');
      return;
    }

    if (!selectedTeacher) {
      setError('Please select your name');
      return;
    }

    if (!password) {
      setError('Please enter your password');
      return;
    }

    setIsLoading(true);
    setLoadingMessage('Connecting to server...');

    // Warm up the database (non-blocking, don't wait)
    fetch(`${API_URL}/api/warmup`).catch(() => {});
    
    try {
      // Find the selected school and teacher from current state
      const school = schools.find(s => s.id === selectedSchool);
      if (!school) {
        setError('Selected school not found. Please refresh and try again.');
        setIsLoading(false);
        setLoadingMessage('');
        return;
      }

      // Find teacher within the selected school's teachers list
      const teacher = school.teachers.find(t => t.id === selectedTeacher);
      
      if (!teacher) {
        setError('Selected teacher not found in this school. Please select again.');
        setIsLoading(false);
        setLoadingMessage('');
        return;
      }

      console.log('Login attempt:', { 
        school: school.name, 
        teacher: teacher.name, 
        email: teacher.email 
      });

      setLoadingMessage('Verifying password...');

      // Authenticate user via backend API
      const response = await apiService.login(teacher.email, password);
      
      if (response.user) {
        // Store user data with school info for sidebar logic
        const userWithSchool = {
          ...response.user,
          school_id: school.id,
          school_name: school.name,
          school_type: school.school_type || response.user.school_type
        };
        
        localStorage.setItem('currentUser', JSON.stringify(userWithSchool));
        localStorage.setItem('user', JSON.stringify(userWithSchool)); // For backward compatibility
        
        // Also store token separately for redundancy
        if (response.access_token) {
          localStorage.setItem('token', response.access_token);
          localStorage.setItem('authToken', response.access_token);
        }
        
        // Dispatch custom event to notify LanguageProvider of user change
        window.dispatchEvent(new Event('userLoggedIn'));
        
        console.log('Login successful:', userWithSchool.name, 'at', userWithSchool.school_name);
        
        setIsLoading(false);
        navigate('/dashboard');
      } else {
        setError('Login failed. Please try again.');
        setIsLoading(false);
        setLoadingMessage('');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.message === 'Invalid credentials') {
        setError('Incorrect password. Please check and try again.');
      } else if (err.message === 'Failed to fetch' || err.name === 'AbortError' || err.message === 'Request failed') {
        // Auto-retry up to 2 more times with increasing delays
        for (let retry = 1; retry <= 2; retry++) {
          try {
            setLoadingMessage(`Reconnecting... (attempt ${retry})`);
            await new Promise(r => setTimeout(r, 4000 * retry));
            const school = schools.find(s => s.id === selectedSchool);
            const teacher = school?.teachers?.find(t => t.id === selectedTeacher);
            if (teacher) {
              const retryResponse = await apiService.login(teacher.email, password);
              if (retryResponse && retryResponse.access_token) {
                localStorage.setItem('authToken', retryResponse.access_token);
                localStorage.setItem('token', retryResponse.access_token);
                localStorage.setItem('currentUser', JSON.stringify(retryResponse.user));
                localStorage.setItem('user', JSON.stringify(retryResponse.user));
                navigate('/dashboard');
                return;
              }
            }
          } catch (retryErr) {
            console.error(`Retry ${retry} failed:`, retryErr);
            if (retry === 2) break;
          }
        }
        setError('Connection issue. Please wait a moment and try again.');
      } else if (err.message === 'Request timed out. Please try again.') {
        setError('Server took too long to respond. Please try again.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid md:grid-cols-2 gap-8 items-center">
        
        {/* Left Side - Branding with Background Image */}
        <div className="hidden md:block relative rounded-2xl overflow-hidden min-h-[600px]">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('/login-background.jpg')`,
            }}
          >
            {/* Gradient Overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          </div>
          
          {/* Content on top of image */}
          <div className="relative z-10 p-8 h-full flex flex-col justify-between">
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-16 h-16 bg-white/90 backdrop-blur rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-3xl font-bold text-blue-600">P</span>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-amber-300 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">Penama Education Portal</h1>
                  <p className="text-amber-100 font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">Penama Province, Vanuatu</p>
                </div>
              </div>

              <div className="space-y-4 mt-12">
                <div className="flex items-start space-x-3 bg-black/30 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="w-10 h-10 bg-white/90 rounded-lg flex items-center justify-center flex-shrink-0">
                    <School className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-amber-300 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">Multi-School System</h3>
                    <p className="text-sm text-gray-100 font-medium">Access from 6 pilot schools across Penama Province</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 bg-black/30 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="w-10 h-10 bg-white/90 rounded-lg flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-amber-300 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">Easy Login</h3>
                    <p className="text-sm text-gray-100 font-medium">Select your school and name - no typing required!</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 bg-black/30 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="w-10 h-10 bg-white/90 rounded-lg flex items-center justify-center flex-shrink-0">
                    <KeyRound className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-amber-300 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">Secure Access</h3>
                    <p className="text-sm text-gray-100 font-medium">Your personal password keeps your data safe</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl">Teacher & Staff Login</CardTitle>
            <CardDescription>
              Select your school and name to login quickly
            </CardDescription>
          </CardHeader>

          <CardContent>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700" data-testid="login-error-message">{error}</p>
              </div>
            )}

            {loadingSchools ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-3">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <p className="text-sm text-gray-500">Connecting to server...</p>
              </div>
            ) : schools.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <AlertCircle className="w-10 h-10 text-amber-500" />
                <p className="text-sm text-gray-600 text-center">Server is not responding. Please wait a moment and try again.</p>
                <Button type="button" onClick={() => { setLoadingSchools(true); fetchSchoolsWithTeachers(); }} variant="outline">
                  Try Again
                </Button>
              </div>
            ) : (
              <form onSubmit={handleLogin} className="space-y-6">
                {/* School Selection - Native select for cross-browser compatibility */}
                <div className="space-y-2">
                  <Label htmlFor="school" className="text-base font-medium">
                    <School className="w-4 h-4 inline mr-2" />
                    Select Your School
                  </Label>
                  <select
                    id="school"
                    data-testid="school-select"
                    value={selectedSchool}
                    onChange={(e) => setSelectedSchool(e.target.value)}
                    className="flex h-12 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Choose your school...</option>
                    {schools.map((school) => (
                      <option key={school.id} value={school.id}>
                        {school.name} ({school.teachers.length} staff)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Teacher Selection - Native select for cross-browser compatibility */}
                <div className="space-y-2">
                  <Label htmlFor="teacher" className="text-base font-medium">
                    <User className="w-4 h-4 inline mr-2" />
                    Select Your Name
                  </Label>
                  <select
                    id="teacher"
                    data-testid="teacher-select"
                    value={selectedTeacher}
                    onChange={(e) => setSelectedTeacher(e.target.value)}
                    disabled={!selectedSchool}
                    className="flex h-12 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">
                      {selectedSchool ? "Choose your name..." : "Select a school first..."}
                    </option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name}{teacher.department ? ` - ${teacher.department}` : ''}{teacher.role === 'principal' ? ' (Principal)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Password Input */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-base font-medium">
                    <KeyRound className="w-4 h-4 inline mr-2" />
                    Enter Your Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="h-12"
                    disabled={!selectedTeacher}
                  />
                </div>

                {/* Login Button */}
                <Button
                  type="submit"
                  disabled={isLoading || !selectedTeacher || !password}
                  className="w-full h-12 text-base"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {loadingMessage || 'Logging in...'}
                    </>
                  ) : (
                    'Login to Dashboard'
                  )}
                </Button>

                {/* Additional Links */}
                <div className="pt-4 border-t">
                  <div className="text-sm text-center space-y-2">
                    <p className="text-gray-600">Other Portals:</p>
                    <div className="flex justify-center space-x-4">
                      <Link to="/coordinator/login" className="text-blue-600 hover:underline">
                        Coordinator
                      </Link>
                      <span className="text-gray-300">|</span>
                      <Link to="/parent/login" className="text-blue-600 hover:underline">
                        Parent Portal
                      </Link>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
